import { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { useAuth } from "react-oidc-context";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";

import { faRotate, faTrashCan, faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Direction, Flex } from "@ntemarked/mittne-components";

import Accordion from "components/molecules/Accordion";
import TextCopyButton from "components/molecules/TextCopyButton";
import ModalButton from "components/organisms/ModalButton";
import useDateTime from "hooks/useDateTime";
import { SectionTitle } from "pages/Features/Statistikk/StatisticsPage";
import DeleteDeviceModal from "pages/Users/EditUserPage/DeleteDeviceModal";
import DeleteUsersModal from "pages/Users/EditUserPage/DeleteUsersModal";
import { useGetUserQuery, useSyncUserMutation } from "pages/Users/Users.graphql-gen";
import { contractIsActive, contractStatusDisplayName, meterHasActiveContract } from "utils/Contract";
import { getUcBaseLink, getUcMeteringvaluesBaseLink } from "utils/Utils";

import { ContractStatus, ElectricityContract, Meter, OwnerType } from "generated/graphql-types";

function isSharedMeter(meter: Meter) {
	return meter.owner?.type === OwnerType.SHARED_PRIVATE || meter.owner?.type === OwnerType.SHARED_COMPANY;
}

// The contract shown on a meter card: prefer active, then processing, then the most recent.
function primaryContract(meter: Meter): ElectricityContract | undefined {
	const contracts = (meter.electricityContracts ?? []).filter(Boolean) as ElectricityContract[];
	return (
		contracts.find((c) => c.status === ContractStatus.ACTIVE) ??
		contracts.find((c) => c.status === ContractStatus.PROCESSING) ??
		contracts.slice().sort((a, b) => Date.parse(b.dateFrom ?? "") - Date.parse(a.dateFrom ?? ""))[0]
	);
}

function contractStatusStyle(status?: ContractStatus) {
	switch (status) {
		case ContractStatus.ACTIVE:
			return { labelBg: "var(--varsel-medium-groenn)", labelColor: "#244030", cardBg: "#F3F9F3" };
		case ContractStatus.PROCESSING:
		case ContractStatus.WAITING_TRANSFER:
		case ContractStatus.IN_TAKEOVER:
		case ContractStatus.CEASED:
		case ContractStatus.TO_BE_CEASED:
			return { labelBg: "var(--nte-korall)", labelColor: "#811717", cardBg: "#FEEEEA" };
		default:
			return { labelBg: "var(--nte-graa-lys)", labelColor: "var(--nte-sort)", cardBg: "var(--nte-graa-lys-50)" };
	}
}

export default function UserPage() {
	const { format } = useDateTime();
	const { userId } = useParams<{ userId: string }>();
	const auth = useAuth();

	const { data, loading } = useGetUserQuery({
		variables: {
			userId: userId ?? "",
		},
	});

	const user = data?.user;
	const [syncUser, { loading: syncUsersLoading }] = useSyncUserMutation();

	const currentContract = useMemo(() => {
		if (user?.ownedContracts?.length === 0) return undefined;
		const activeContract = user?.ownedContracts?.filter((c) => contractIsActive(c))[0];
		if (activeContract) return activeContract;
		const inactiveContract = user?.ownedContracts?.filter((c) => !contractIsActive(c))[0];
		return inactiveContract;
	}, [user?.ownedContracts]);

	const { ownMeters, sharedMeters } = useMemo(() => {
		const meters = (user?.meters ?? []).filter(Boolean) as Meter[];
		const byActiveFirst = (a: Meter, b: Meter) => Number(meterHasActiveContract(b)) - Number(meterHasActiveContract(a));
		return {
			ownMeters: meters.filter((m) => !isSharedMeter(m)).sort(byActiveFirst),
			sharedMeters: meters.filter(isSharedMeter).sort(byActiveFirst),
		};
	}, [user?.meters]);

	async function openAppView() {
		try {
			const keycloakAuthority = import.meta.env.VITE_OIDC_AUTHORITY;
			const keycloakRealm = keycloakAuthority.split("/realms/")[1];
			const accessToken = auth.user?.access_token;
			if (!user?.idProviderSubject) {
				throw new Error("Fant ikke keycloak id");
			}
			const impersonationResponse = await fetch(
				`${keycloakAuthority.replace("/realms/" + keycloakRealm, "")}/admin/realms/${keycloakRealm}/users/${user?.idProviderSubject}/impersonation`,
				{
					method: "POST",
					credentials: "include",
					headers: {
						"Content-Type": "application/json",
						"Authorization": `Bearer ${accessToken}`,
					},
				}
			);
			if (!impersonationResponse.ok) {
				throw new Error(`Keycloak impersonation failed: ${impersonationResponse.status}`);
			}
			const params = `width=390,height=844,left=100,top=100`;
			const appUrl = new URL(import.meta.env.VITE_MITT_NTE_APP_BASE_URL);
			appUrl.searchParams.set("autoLogIn", "true");
			// Sett denne til localhost:3000 om du vil teste lokalt
			window.open(appUrl.toString(), `${user?.name} App`, params);
			setTimeout(() => {
				auth.removeUser();
			}, 10000);
		} catch (error) {
			console.error("Feil i åpning av appview:", error);
			alert(error);
		}
	}

	async function synchronizeUser() {
		const syncUsersResponse = await syncUser({
			variables: {
				userId: userId ?? "",
			},
			refetchQueries: ["getUser"],
		});
		if (!syncUsersResponse.data?.syncUser?.success) {
			alert("Synkronisering feilet...");
		}
	}

	return (
		<Flex direction={Direction.row} gap="var(--grid-2)">
			<ContentWrapper>
				<TopSection>
					<div style={{ flexGrow: 1 }}>
						<NameRow>
							<PageName>
								{user?.firstName && user?.lastName ? (
									`${user.firstName} ${user.lastName}`
								) : (
									<u>Åpen bruker {user?.nickname ? `(${user.nickname})` : ""}</u>
								)}
							</PageName>
							{currentContract && (
								<a
									onClick={(e) => e.stopPropagation()}
									title="Se kunde i UC"
									href={`${getUcBaseLink()}/#/customer/${currentContract.customerGuid}/contracts/list`}
									target="_blank"
									rel="noreferrer"
								>
									<img src="/assets/icons/uc-logo.svg" alt="Kundedetaljer i UtilityCloud" style={{ height: "1.25rem", width: "auto" }} />
								</a>
							)}
						</NameRow>
						<ContactLine>
							Mobil: {loading ? <Skeleton width={120} /> : user?.mobileNumber || "―"}{" "}
							{!loading && user?.mobileNumber && <TextCopyButton text={user.mobileNumber} color="var(--nte-graa-500)" />}
						</ContactLine>
						<ContactLine>
							E-post: {loading ? <Skeleton width={180} /> : user?.emailAddress || "―"}{" "}
							{!loading && user?.emailAddress && <TextCopyButton text={user.emailAddress} color="var(--nte-graa-500)" />}
						</ContactLine>
					</div>
					<PrimaryPillButton
						onClick={async () => openAppView()}
						disabled={!user?.idProviderSubject}
						title={user?.idProviderSubject ? "Åpne appvisning (logger deg ut)" : "Bruker har ikke logget inn med BankID"}
					>
						Åpne appvisning <FontAwesomeIcon icon={faUpRightFromSquare} />
					</PrimaryPillButton>
				</TopSection>
				<div style={{ marginTop: "24px" }}>

					<AvtaleSection title="Egne avtaler" meters={ownMeters} shared={false} />
					<AvtaleSection title="Delte avtaler" meters={sharedMeters} shared={true} />
					<SectionTitle>Enheter</SectionTitle>
					{!user?.devices && <Accordion loading={loading} />}
					{user?.devices && user.devices.length === 0 && <p>Ingen enheter å vise</p>}
					<CardList>
						{user?.devices
							?.slice()
							.sort((a, b) => {
								if (!a || !b || a.lastSeen === b.lastSeen) return 0;
								return Date.parse(a.lastSeen as string) < Date.parse(b.lastSeen as string) ? 1 : -1;
							})
							.map((device) => (
								<Card style={{ background: "var(--nte-blaa-himmel)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }} key={device.deviceId}>
									<div>
										<CardTitle>{[device.operatingSystem, device.osVersion, device.manufacturer, device.model].filter(Boolean).join(" ")}</CardTitle>
										<DeviceMeta>
											<Pill>Versjon {device.appVersion || "Ukjent"}</Pill>
											<span>Sist aktiv i app: {device.lastSeen ? format(device.lastSeen, "dd.MM.yyyy HH:mm") : "Ukjent"}</span>
										</DeviceMeta>
									</div>
									<OutlinePillModalButton label="Slett enhet" title="Slett enhet" icon={<FontAwesomeIcon icon={faTrashCan} />}>
										<DeleteDeviceModal user={user} device={device} />
									</OutlinePillModalButton>
								</Card>
							))}
					</CardList>

					<PageFooter>
						<FooterCol>
							<FooterText>
								Sist synkronisert {user?.lastUpdated ? format(user.lastUpdated, "dd.MM.yyyy 'kl.' HH:mm") : "―"}
							</FooterText>
							<OutlinePillButton disabled={syncUsersLoading} onClick={async () => synchronizeUser()}>
								Synkroniser nå <FontAwesomeIcon spin={syncUsersLoading} icon={faRotate} />
							</OutlinePillButton>
						</FooterCol>
						{!!user && (
							<FooterCol>
								<FooterText>App-bruker{user?.idProviderSubject ? "" : " (ikke innlogget med BankID)"}</FooterText>
								<OutlinePillModalButton label="Slett app-bruker" title="Slett app-bruker" icon={<FontAwesomeIcon icon={faTrashCan} />}>
									<DeleteUsersModal userToDelete={user} />
								</OutlinePillModalButton>
							</FooterCol>
						)}
					</PageFooter>
				</div>
			</ContentWrapper>
		</Flex>
	);

	function AvtaleSection({ title, meters, shared }: Readonly<{ title: string; meters: Meter[]; shared: boolean }>) {
		return (
			<div>
				<SectionTitle>{title}</SectionTitle>
				{!user?.meters && <Accordion loading={loading} />}
				{user?.meters && meters.length === 0 && <p>Ingen avtaler å vise</p>}
				<CardList>
					{meters.map((meter) => (
						<AvtaleCard meter={meter} shared={shared} key={meter.meteringPointId} />
					))}
				</CardList>
			</div>
		);
	}

	function AvtaleCard({ meter, shared }: Readonly<{ meter: Meter; shared: boolean }>) {
		const contract = primaryContract(meter);
		const status = contractStatusStyle(contract?.status);
		const fjutt = user?.fjutter?.find((f) => f?.anleggsId === meter.meteringPointId || f?.anleggsId === meter.meterId);
		const startWord = contract?.status === ContractStatus.PROCESSING ? "starter" : "siden";

		return (
			<Card style={{ background: status.cardBg }}>
				<CardHeader>
					<CardTitle>
						{meter.address?.streetAddress}, {meter.address?.postalCode} {meter.address?.postalPlace}
						{meter.alias ? ` (${meter.alias})` : ""}
					</CardTitle>
					{contract && (
						<StatusLabel style={{ background: status.labelBg, color: status.labelColor }}>
							{contractStatusDisplayName(contract.status)}
						</StatusLabel>
					)}
				</CardHeader>

				{shared && meter.owner?.nameOfOwner && (
					<CardLine>
						Eier: <OwnerName>{meter.owner.nameOfOwner}</OwnerName>
					</CardLine>
				)}

				{contract && (
					<CardLine>
						Avtale:{" "}
						<a href={`${getUcBaseLink()}/#/products/edit/${contract.productCode}`} target="_blank" rel="noreferrer">
							{contract.productName}
						</a>
						{contract.dateFrom && ` (${startWord} ${format(contract.dateFrom, "dd.MM.yy")})`}
					</CardLine>
				)}

				<CardLine>
					MPID:{" "}
					<a href={`${getUcMeteringvaluesBaseLink()}/admin/events/maalerpunkt/${meter.meteringPointId}`} target="_blank" rel="noreferrer">
						{meter.meteringPointId}
					</a>{" "}
					<TextCopyButton text={meter.meteringPointId ?? ""} color="var(--nte-blaa-logo)" />
				</CardLine>

				<LabelRow>
					{meter.gridOwner && <Pill>{meter.gridOwner}</Pill>}
					{meter.priceZone && <Pill>{meter.priceZone}</Pill>}
					{meter.classification?.classification && (
						<Pill>
							{meter.classification.classification}
							{meter.classification.consumptionCode ? ` ${meter.classification.consumptionCode}` : ""}
						</Pill>
					)}
				</LabelRow>

				{fjutt && (
					<HarFjuttLink href={`https://admin.enerlytics.no/devices/${fjutt.deviceId}`} target="_blank" rel="noreferrer">
						Har Fjutt <FontAwesomeIcon icon={faUpRightFromSquare} />
					</HarFjuttLink>
				)}
			</Card>
		);
	}
}



const pill = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	border-radius: 30px;
	font-size: 12px;
	font-weight: 500;
	letter-spacing: 0.3px;
	white-space: nowrap;
	cursor: pointer;
	height: auto;
`;

const outlinePill = css`
	${pill}
	height: 32px;
	padding: 0 16px;
	background: var(--white);
	color: #056089;
	border: 1px solid var(--nte-blaa-logo);
	&:hover {
		background: var(--nte-blaa-himmel);
		color: #056089;
	}
`;

const ContentWrapper = styled.div`
	flex-grow: 1;
	max-width: var(--max-textbox-width);
`;

const CardList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const Card = styled.div`
	border-radius: 4px;
	padding: 16px 20px;
`;

const CardHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
`;

const CardTitle = styled.p`
	font-weight: 700;
	font-size: 16px;
	color: var(--nte-sort);
	margin: 0;
`;

const StatusLabel = styled.span`
	font-size: 12px;
	font-weight: 500;
	line-height: 1;
	letter-spacing: 0.3px;
	padding: 4px 8px;
	border-radius: 4px;
	white-space: nowrap;
`;

const CardLine = styled.p`
	font-size: 16px;
	margin: 6px 0 0;
`;

const OwnerName = styled.span`
	color: var(--nte-blaa-logo);
`;

const LabelRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 12px;
`;

const DeviceMeta = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-top: 6px;
	font-size: 16px;
`;

const Pill = styled.span`
	background: var(--nte-blaa-300);
	color: var(--nte-blaa-mork);
	font-size: 12px;
	font-weight: 500;
	line-height: 1;
	letter-spacing: 0.3px;
	padding: 4px 8px;
	border-radius: 4px;
	white-space: nowrap;
`;

const HarFjuttLink = styled.a`
	${outlinePill}
	width: max-content;
	margin-top: 12px;
	text-decoration: none;
`;

const TopSection = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: var(--grid-2);
`;

const NameRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 8px;
`;

const PageName = styled.h1`
	font-size: 30px;
	font-weight: 600;
	line-height: 1.4;
	margin: 0;
`;

const ContactLine = styled.p`
	font-size: 16px;
	margin: 2px 0;
`;

const PrimaryPillButton = styled.button`
	${pill}
	height: 48px;
	padding: 0 37px;
	background: var(--nte-blaa-logo);
	color: var(--white);
	border: 1px solid var(--nte-blaa-logo);
	&:hover {
		background: var(--nte-blaa-uu);
		border-color: var(--nte-blaa-uu);
		color: var(--white);
	}
`;

const OutlinePillButton = styled.button`
	${outlinePill}
`;

const OutlinePillModalButton = styled(ModalButton)`
	${outlinePill}
`;

const PageFooter = styled.div`
	display: flex;
	gap: 80px;
	margin-top: 40px;
`;

const FooterCol = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: flex-start;
`;

const FooterText = styled.span`
	font-size: 16px;
`;
